import React, { useState, useEffect } from 'react';
import {
  Alert,
  Table,
  Button,
  Space,
  Tag,
  Popconfirm,
  message,
  Modal,
  Select,
  Typography,
  Tabs,
} from 'antd';
import { PlusOutlined, DeleteOutlined, CreditCardOutlined, UserOutlined } from '@ant-design/icons';
import { SubscriptionService } from '../../services/api/subscriptionService';
import { UserService, User } from '../../services/api/userService';
import { CompanyInstanceService } from '../../services/api/companyInstanceService';
import { CompanySubscription, Subscription, UserSubscription, SubscriptionType } from '../../types/subscription';
import { SubscriptionStatusBadge } from '../SubscriptionManagement/SubscriptionStatusBadge';
import { getApiErrorMessage } from '../../utils/apiErrors';

const { Text } = Typography;
const { Option } = Select;

interface CompanySubscriptionTabProps {
  companyId: string;
}

export const CompanySubscriptionTab: React.FC<CompanySubscriptionTabProps> = ({ companyId }) => {
  const [subscriptions, setSubscriptions] = useState<CompanySubscription[]>([]);
  const [allSubscriptions, setAllSubscriptions] = useState<Subscription[]>([]);
  const [subscriptionTypes, setSubscriptionTypes] = useState<SubscriptionType[]>([]);
  const [loading, setLoading] = useState(false);
  const [isAssignModalVisible, setIsAssignModalVisible] = useState(false);
  const [selectedSubscriptionTypeCode, setSelectedSubscriptionTypeCode] = useState<string>('');
  
  // User subscription assignment state
  const [companyUsers, setCompanyUsers] = useState<User[]>([]);
  const [isUserAssignModalVisible, setIsUserAssignModalVisible] = useState(false);
  const [selectedSubscriptionForUser, setSelectedSubscriptionForUser] = useState<string>('');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [userSubscriptions, setUserSubscriptions] = useState<UserSubscription[]>([]);

  useEffect(() => {
    if (companyId) {
      void loadCompanySubscriptions();
      void loadCompanyUsers();
      void loadSubscriptionTypes();
    }
  }, [companyId]);

  useEffect(() => {
    if (subscriptions.length > 0) {
      void loadUserSubscriptions();
    } else {
      setUserSubscriptions([]);
    }
  }, [subscriptions]);

  const loadCompanySubscriptions = async () => {
    setLoading(true);
    try {
      const companySubs = await SubscriptionService.getCompanySubscriptions(companyId);
      console.log('[CompanySubscriptionTab] Loaded company subscriptions:', companySubs.map(cs => ({
        id: cs.id,
        subscription_id: cs.subscription_id,
        company_id: cs.company_id,
        subscription_type_id: (cs as any).subscription_type_id,
      })));
      setSubscriptions(companySubs);
      
      // Get subscription details for each company subscription
      // The backend may return subscription_type_id directly in CompanySubscription
      const subscriptionDetails: Subscription[] = [];
      for (const cs of companySubs) {
        try {
          // Try to get full subscription details using company subscription endpoint
          // The ID to use might be cs.id (company subscription ID) or cs.subscription_id
          let sub: Subscription | null = null;
          
          // First try using the company subscription endpoint with the company subscription ID
          if (cs.id) {
            try {
              sub = await SubscriptionService.getCompanySubscriptionById(companyId, cs.id);
            } catch (err) {
              // If that fails, try with subscription_id
              if (cs.subscription_id) {
                try {
                  sub = await SubscriptionService.getCompanySubscriptionById(companyId, cs.subscription_id);
                } catch (err2) {
                  // Last resort: try generic subscription endpoint with context (ISSUE-110 fix)
                  if (cs.subscription_id) {
                    sub = await SubscriptionService.getById(cs.subscription_id, { companyId });
                  }
                }
              }
            }
          }
          
          // If we still don't have subscription details but have subscription_type_id, 
          // create a minimal subscription object with the type info
          if (!sub && (cs as any).subscription_type_id) {
            const subscriptionType = subscriptionTypes.find(t => t.id === (cs as any).subscription_type_id);
            if (subscriptionType) {
              // Create a minimal subscription object for display
              sub = {
                id: cs.subscription_id || cs.id,
                subscription_type_id: (cs as any).subscription_type_id,
                subscription_type: subscriptionType,
                status: (cs as any).status || 'active',
                company_id: companyId,
                created_at: cs.assigned_at,
                updated_at: cs.assigned_at,
              } as Subscription;
            }
          }
          
          if (sub) {
            subscriptionDetails.push(sub);
          }
        } catch (err) {
          console.warn(`Failed to fetch subscription details for company subscription ${cs.id}:`, err);
        }
      }
      setAllSubscriptions(subscriptionDetails);
    } catch (error: unknown) {
      console.error('Error loading company subscriptions:', error);
      message.error(getApiErrorMessage(error, 'Failed to load subscriptions'));
    } finally {
      setLoading(false);
    }
  };

  const handleUnassign = async (subscriptionId: string, companySubscriptionId?: string) => {
    try {
      // The backend might expect the subscription ID, but if subscription_id is undefined,
      // we might need to use the company subscription ID (record.id) instead
      // Try using the provided subscriptionId first, but fall back to companySubscriptionId if subscriptionId is undefined
      const idToUse = subscriptionId || companySubscriptionId;
      if (!idToUse) {
        console.error('Cannot unassign: subscription ID is missing', { subscriptionId, companySubscriptionId });
        message.error('Cannot unassign: subscription ID is missing');
        return;
      }
      console.log('Unassigning subscription:', { subscriptionId, companySubscriptionId, idToUse, companyId });
      await SubscriptionService.unassignCompany(idToUse, companyId, companySubscriptionId);
      message.success('Subscription unassigned successfully');
      await loadCompanySubscriptions();
    } catch (error: any) {
      console.error('Error unassigning subscription:', error);
      message.error(error.message || 'Failed to unassign subscription');
    }
  };

  const loadSubscriptionTypes = async () => {
    try {
      const types = await SubscriptionService.getSubscriptionTypes();
      setSubscriptionTypes(types.filter(type => type.is_active));
    } catch (error) {
      console.error('Error loading subscription types:', error);
      message.error('Failed to load subscription types');
    }
  };

  const handleAssign = async () => {
    if (!selectedSubscriptionTypeCode) {
      message.warning('Please select a subscription type');
      return;
    }

    try {
      setLoading(true);
      
      // Find the subscription type by code to get its ID
      const subscriptionType = subscriptionTypes.find(
        type => type.code === selectedSubscriptionTypeCode
      );
      
      if (!subscriptionType) {
        message.error('Subscription type not found');
        return;
      }

      // Create a company subscription using the correct endpoint
      const periodStartDate = new Date().toISOString();
      
      await SubscriptionService.createCompanySubscription({
        company_id: companyId,
        subscription_type_id: subscriptionType.id,
        period_start_date: periodStartDate,
        is_active: true,
      });
      
      message.success('Subscription created and assigned successfully');
      setIsAssignModalVisible(false);
      setSelectedSubscriptionTypeCode('');
      await loadCompanySubscriptions();
    } catch (error: any) {
      // Suppress CORS/network errors
      const isCorsError = error.code === 'ERR_NETWORK' || 
                         error.message?.includes('CORS') ||
                         error.message?.includes('Access-Control-Allow-Origin');
      
      if (isCorsError) {
        console.warn('CORS error when creating subscription (backend configuration issue):', error);
        message.error('Network error: Unable to create subscription. Please check backend CORS configuration.');
      } else {
        console.error('Error creating subscription:', error);
        message.error(getApiErrorMessage(error, 'Failed to create company subscription'));
      }
    } finally {
      setLoading(false);
    }
  };

  const loadCompanyUsers = async () => {
    try {
      // Get company instances to find users
      const instances = await CompanyInstanceService.getAll({ company_id: companyId });
      
      // Get full user objects for each instance
      const users: User[] = [];
      for (const instance of instances) {
        try {
          const user = await UserService.getById(instance.user_id);
          if (user) {
            users.push(user);
          }
        } catch (err) {
          // Skip if user not found
          console.warn(`User ${instance.user_id} not found for company instance ${instance.id}`);
        }
      }
      setCompanyUsers(users);
    } catch (error: any) {
      // Suppress CORS/network errors
      const isCorsError = error.code === 'ERR_NETWORK' || 
                         error.message?.includes('CORS') ||
                         error.message?.includes('Access-Control-Allow-Origin');
      if (!isCorsError) {
        console.error('Error loading company users:', error);
      }
    }
  };

  const loadUserSubscriptions = async () => {
    try {
      // Seats are on company subscription rows (layer C), not user-owned plans.
      const allUserSubs: UserSubscription[] = [];
      for (const sub of subscriptions) {
        const companySubId = sub.id || sub.subscription_id;
        if (!companySubId) continue;
        try {
          const seats = await SubscriptionService.getCompanySubscriptionSeats(companySubId);
          allUserSubs.push(
            ...seats.map((seat) => ({
              ...seat,
              subscription_id: seat.subscription_id || companySubId,
              assigned_at: seat.assigned_at || new Date().toISOString(),
              is_active: seat.is_active !== false,
            }))
          );
        } catch {
          /* skip missing seat list */
        }
      }
      setUserSubscriptions(allUserSubs);
    } catch (error) {
      console.error('Error loading subscription seats:', error);
    }
  };

  const handleAssignToUsers = async () => {
    if (!selectedSubscriptionForUser || selectedUserIds.length === 0) {
      message.warning('Please select a subscription and at least one user');
      return;
    }

    try {
      setLoading(true);
      await Promise.all(
        selectedUserIds.map((userId) =>
          SubscriptionService.assignUser(selectedSubscriptionForUser, { user_id: userId })
        )
      );
      message.success(`Assigned subscription to ${selectedUserIds.length} user(s)`);
      setIsUserAssignModalVisible(false);
      setSelectedSubscriptionForUser('');
      setSelectedUserIds([]);
      await loadUserSubscriptions();
    } catch (error: any) {
      message.error(error.message || 'Failed to assign subscription to users');
    } finally {
      setLoading(false);
    }
  };

  const handleUnassignFromUser = async (subscriptionId: string, userId: string) => {
    try {
      await SubscriptionService.unassignUser(subscriptionId, userId);
      message.success('User unassigned successfully');
      await loadUserSubscriptions();
    } catch (error: any) {
      message.error(error.message || 'Failed to unassign user');
    }
  };

  // Get available subscription types (those not already assigned to the company)
  const assignedSubscriptionTypeIds = new Set(
    allSubscriptions.map(sub => sub.subscription_type_id).filter(Boolean)
  );
  const availableSubscriptionTypes = subscriptionTypes.filter(
    (type) => !assignedSubscriptionTypeIds.has(type.id)
  );
  
  const userSubscriptionColumns = [
    {
      title: 'User',
      key: 'user',
      render: (record: UserSubscription) => {
        const user = companyUsers.find(u => u.id === record.user_id);
        return user?.username || user?.email || (record.user_id ? record.user_id.substring(0, 8) : 'N/A');
      },
    },
    {
      title: 'Subscription',
      key: 'subscription',
      render: (record: UserSubscription) => {
        const subscription = allSubscriptions.find(s => s.id === record.subscription_id);
        return subscription?.subscription_type?.name || (record.subscription_id ? record.subscription_id.substring(0, 8) : 'N/A');
      },
    },
    {
      title: 'Assigned At',
      dataIndex: 'assigned_at',
      key: 'assigned_at',
      render: (date: string) => new Date(date).toLocaleString(),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: UserSubscription) => (
        <Popconfirm
          title="Are you sure you want to unassign this subscription from the user?"
          onConfirm={() => handleUnassignFromUser(record.subscription_id, record.user_id)}
        >
          <Button type="link" danger size="small" icon={<DeleteOutlined />}>
            Unassign
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const columns = [
    {
      title: 'Subscription',
      key: 'subscription',
      render: (record: CompanySubscription) => {
        // Try to find subscription by matching IDs
        const subscription = allSubscriptions.find((s) => 
          s.id === record.subscription_id || 
          s.id === record.id ||
          (s as any).subscription_id === record.subscription_id
        );
        
        // Check if we have subscription_type_id directly in the record (from backend response)
        const subscriptionTypeId = (record as any).subscription_type_id;
        const subscriptionType = subscriptionTypeId 
          ? subscriptionTypes.find(t => t.id === subscriptionTypeId)
          : subscription?.subscription_type;
        
        const subscriptionTypeName = subscriptionType?.name || 
                                    subscription?.subscription_type?.name ||
                                    (record as any).subscription_type_name;
        
        const displayName = subscriptionTypeName || 
                           (record.subscription_id ? `Subscription ${record.subscription_id.substring(0, 8)}...` : 
                           (record.id ? `Subscription ${record.id.substring(0, 8)}...` : 'Unknown Subscription'));
        
        return (
          <Space>
            <CreditCardOutlined />
            <Text strong>
              {displayName}
            </Text>
            {subscription && <SubscriptionStatusBadge status={subscription.status} />}
          </Space>
        );
      },
    },
    {
      title: 'Type',
      key: 'type',
      render: (record: CompanySubscription) => {
        // Try to find subscription by matching IDs
        const subscription = allSubscriptions.find((s) => 
          s.id === record.subscription_id || 
          s.id === record.id ||
          (s as any).subscription_id === record.subscription_id
        );
        
        // Check if we have subscription_type_id directly in the record (from backend response)
        const subscriptionTypeId = (record as any).subscription_type_id;
        const subscriptionType = subscriptionTypeId 
          ? subscriptionTypes.find(t => t.id === subscriptionTypeId)
          : subscription?.subscription_type;
        
        const subscriptionTypeCode = subscriptionType?.code || 
                                    subscription?.subscription_type?.code ||
                                    (record as any).subscription_type_code;
        
        return subscriptionTypeCode || 'N/A';
      },
    },
    {
      title: 'Features',
      key: 'features',
      render: (record: CompanySubscription) => {
        const subscriptionTypeId = (record as any).subscription_type_id as string | undefined;
        const type =
          (subscriptionTypeId
            ? subscriptionTypes.find((t) => t.id === subscriptionTypeId)
            : undefined) ||
          allSubscriptions.find((s) => s.id === record.id || s.id === record.subscription_id)
            ?.subscription_type;
        const codes = type?.features?.map((f) => f.code).filter(Boolean) ?? [];
        if (!codes.length) return <Text type="secondary">—</Text>;
        return (
          <Space size={[4, 4]} wrap>
            {codes.slice(0, 5).map((c) => (
              <Tag key={c}>{c}</Tag>
            ))}
            {codes.length > 5 ? <Tag>+{codes.length - 5}</Tag> : null}
          </Space>
        );
      },
    },
    {
      title: 'Assigned At',
      dataIndex: 'assigned_at',
      key: 'assigned_at',
      render: (date: string) => (date ? new Date(date).toLocaleString() : '—'),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: CompanySubscription) => {
        // Try to find the subscription in our loaded details to get the correct subscription ID
        const subscription = allSubscriptions.find((s) => 
          s.id === record.subscription_id || 
          s.id === record.id ||
          (s as any).subscription_id === record.subscription_id
        );
        
        // Use subscription ID from the subscription object if available, otherwise fall back to record fields
        const subscriptionId = subscription?.id || record.subscription_id || record.id;
        
        if (!subscriptionId) {
          console.error('[CompanySubscriptionTab] Cannot determine subscription ID for unassign:', record);
        }
        
        return (
          <Popconfirm
            title="Are you sure you want to unassign this subscription?"
            onConfirm={() => handleUnassign(subscriptionId, record.id)}
          >
            <Button type="link" danger size="small" icon={<DeleteOutlined />}>
              Unassign
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  return (
    <div data-testid="company-subscription-tab">
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Company-owned subscription + seats"
        description="Add a company plan (layer B) with POST /subscriptions/companies. User Assignments are seats on that company plan (layer C), not user-owned subscriptions."
      />
      <Tabs
        defaultActiveKey="company"
        items={[
          {
            key: 'company',
            label: (
              <span>
                <CreditCardOutlined />
                Company Subscriptions
              </span>
            ),
            children: (
              <Space direction="vertical" style={{ width: '100%' }} size="large">
                <div>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setIsAssignModalVisible(true)}
                  >
                    Add company subscription
                  </Button>
                </div>

                <Table
                  columns={columns}
                  dataSource={subscriptions}
                  rowKey="id"
                  loading={loading}
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} subscriptions`,
                  }}
                />
              </Space>
            ),
          },
          {
            key: 'users',
            label: (
              <span>
                <UserOutlined />
                User Assignments ({userSubscriptions.length})
              </span>
            ),
            children: (
              <Space direction="vertical" style={{ width: '100%' }} size="large">
                <div>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setIsUserAssignModalVisible(true)}
                  >
                    Assign Subscription to Company Users
                  </Button>
                </div>

                <Table
                  columns={userSubscriptionColumns}
                  dataSource={userSubscriptions}
                  rowKey={(record) => `${record.subscription_id}-${record.user_id}`}
                  loading={loading}
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} user assignments`,
                  }}
                />
              </Space>
            ),
          },
        ]}
      />

      {/* Assign Subscription to Company Modal */}
      <Modal
        title="Assign Subscription to Company"
        open={isAssignModalVisible}
        onOk={handleAssign}
        onCancel={() => {
          setIsAssignModalVisible(false);
          setSelectedSubscriptionTypeCode('');
        }}
        confirmLoading={loading}
        okText="Assign"
        cancelText="Cancel"
      >
        <Select
          style={{ width: '100%' }}
          placeholder="Select a subscription type"
          value={selectedSubscriptionTypeCode || undefined}
          onChange={setSelectedSubscriptionTypeCode}
          showSearch
          filterOption={(input, option) =>
            (option?.label as string || '')
              ?.toLowerCase()
              .includes(input.toLowerCase())
          }
        >
          {availableSubscriptionTypes.map((type) => {
            const label = `${type.name} (${type.code}) - ${type.billing_period}${type.base_price ? ` - $${type.base_price} ${type.currency}` : ''}`;
            return (
              <Option key={type.id} value={type.code} label={label}>
                {label}
              </Option>
            );
          })}
        </Select>
        {availableSubscriptionTypes.length === 0 && (
          <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            No available subscription types. All subscription types are already assigned to this company.
          </Text>
        )}
      </Modal>

      {/* Assign Subscription to Company Users Modal */}
      <Modal
        title="Assign Subscription to Company Users"
        open={isUserAssignModalVisible}
        onOk={handleAssignToUsers}
        onCancel={() => {
          setIsUserAssignModalVisible(false);
          setSelectedSubscriptionForUser('');
          setSelectedUserIds([]);
        }}
        confirmLoading={loading}
        okText="Assign"
        cancelText="Cancel"
        width={600}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text strong>Select Subscription:</Text>
            <Select
              style={{ width: '100%', marginTop: 8 }}
              placeholder="Select a subscription"
              value={selectedSubscriptionForUser || undefined}
              onChange={setSelectedSubscriptionForUser}
              showSearch
              filterOption={(input, option) =>
                (option?.label as string || '')
                  ?.toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {subscriptions.map((cs) => {
                // Try to find subscription by matching IDs
                const subscription = allSubscriptions.find((s) => 
                  s.id === cs.subscription_id || 
                  s.id === cs.id ||
                  (s as any).subscription_id === cs.subscription_id
                );
                
                // Check if we have subscription_type_id directly in the record (from backend response)
                const subscriptionTypeId = (cs as any).subscription_type_id;
                const subscriptionType = subscriptionTypeId 
                  ? subscriptionTypes.find(t => t.id === subscriptionTypeId)
                  : subscription?.subscription_type;
                
                const subscriptionTypeName = subscriptionType?.name || 
                                            subscription?.subscription_type?.name ||
                                            (cs as any).subscription_type_name ||
                                            cs.subscription_id ||
                                            cs.id;
                
                const subscriptionTypeCode = subscriptionType?.code || 
                                            subscription?.subscription_type?.code ||
                                            (cs as any).subscription_type_code ||
                                            'N/A';
                
                const label = `${subscriptionTypeName} (${subscriptionTypeCode})`;
                const optionKey = cs.subscription_id || cs.id;
                return (
                  <Option key={optionKey} value={cs.subscription_id || cs.id} label={label}>
                    {label}
                  </Option>
                );
              })}
            </Select>
          </div>
          
          <div>
            <Text strong>Select Users:</Text>
            <Select
              mode="multiple"
              style={{ width: '100%', marginTop: 8 }}
              placeholder="Select users to assign subscription"
              value={selectedUserIds}
              onChange={setSelectedUserIds}
              showSearch
              filterOption={(input, option) =>
                (option?.label as string || '')
                  ?.toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {companyUsers.map((user) => {
                const label = `${user.username || user.email} (${user.email})`;
                return (
                  <Option key={user.id} value={user.id} label={label}>
                    {label}
                  </Option>
                );
              })}
            </Select>
          </div>
        </Space>
      </Modal>
    </div>
  );
};

